import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtMoney } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area,
} from "recharts";
import {
  Fuel, TrendingUp, TrendingDown, Truck, Warehouse, PackageCheck, DollarSign,
  ArrowLeftRight, ShoppingCart, Wallet, ReceiptText, CheckSquare, AlertTriangle, Boxes, Loader2,
  RefreshCw, Radio, Activity, CircleDot,
} from "lucide-react";

const GRADE_COLORS = { PMS: "#12346B", AGO: "#F47920", DPK: "#059669", ATK: "#7C3AED" };
const GRADES = ["PMS", "AGO", "DPK", "ATK"];
const gradeOf = (name = "") => GRADES.find((k) => name.includes(k)) || "OTHER";
const pieColor = (name) => GRADE_COLORS[gradeOf(name)] || "#64748B";
const POLL_MS = 20000;

const AXIS_TICK = { fontSize: 11, fill: "#64748B" };

function relTime(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const ACTION_TONE = {
  create: "text-emerald-600", approve: "text-emerald-600", confirm: "text-emerald-600",
  reject: "text-rose-600", delete: "text-rose-600", update: "text-blue-600",
};

function Kpi({ label, value, unit, icon: Icon, tone = "navy", testid, onClick }) {
  const tones = {
    navy: "text-[#12346B]", blue: "text-[#12346B]", amber: "text-orange-600",
    green: "text-emerald-600", red: "text-rose-600", slate: "text-slate-600",
  };
  return (
    <div data-testid={testid} onClick={onClick}
      className={`bg-white border border-slate-200 rounded-lg p-3.5 transition-all ${onClick ? "cursor-pointer hover:shadow-md hover:border-[#12346B]/30 hover:-translate-y-0.5" : "hover:shadow-sm"}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${tones[tone]}`} strokeWidth={1.75} />
      </div>
      <div className={`font-head text-xl font-bold tabular ${tones[tone]}`}>{value}</div>
      {unit && <div className="text-[10px] text-slate-400 mt-0.5">{unit}</div>}
    </div>
  );
}

function TankGauge({ t }) {
  const pct = Math.min(100, (t.current / t.capacity) * 100);
  const color = pieColor(t.product || "");
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={`tank-gauge-${t.tank_no.toLowerCase()}`}>
      <div className="relative w-14 h-28 rounded-md border-2 border-slate-300 bg-slate-50 overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 transition-all duration-700" style={{ height: `${pct}%`, backgroundColor: color, opacity: 0.85 }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[11px] font-bold text-slate-800 tabular bg-white/70 px-1 rounded">{pct.toFixed(0)}%</span>
        </div>
      </div>
      <div className="text-[11px] font-semibold text-slate-700 mono">{t.tank_no}</div>
      <div className="text-[9px] text-slate-400 text-center leading-tight">{(t.product || "").split(" ")[0]}<br />{fmtNum(t.current)} gal</div>
    </div>
  );
}

function Seg({ options, value, onChange, testidPrefix }) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} data-testid={`${testidPrefix}-${o.v}`}
          className={`px-2.5 py-1 text-xs font-semibold rounded transition-colors ${value === o.v ? "bg-[#12346B] text-white" : "text-slate-500 hover:text-slate-800"}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [charts, setCharts] = useState(null);
  const [activity, setActivity] = useState([]);
  const [live, setLive] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [, forceTick] = useState(0);

  const [grade, setGrade] = useState("ALL");
  const [range, setRange] = useState(6);
  const [chartType, setChartType] = useState("bar");

  const timer = useRef(null);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [k, c, a] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/charts"),
        api.get("/dashboard/activity").catch(() => ({ data: { events: [] } })),
      ]);
      setKpi(k.data); setCharts(c.data); setActivity(a.data.events || []);
      setUpdatedAt(Date.now());
    } catch (e) { console.error("Dashboard refresh failed:", e); }
    finally { setRefreshing(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (live) {
      timer.current = setInterval(fetchAll, POLL_MS);
      return () => clearInterval(timer.current);
    }
  }, [live, fetchAll]);

  // re-render every second so "updated Xs ago" / activity times stay fresh
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const flowData = useMemo(() => {
    const f = charts?.monthly_flow || [];
    return range === 0 ? f : f.slice(-range);
  }, [charts, range]);

  const invData = useMemo(() => {
    const d = charts?.inventory_by_grade || [];
    return grade === "ALL" ? d : d.filter((x) => gradeOf(x.name) === grade);
  }, [charts, grade]);

  const tankData = useMemo(() => {
    const t = charts?.tanks || [];
    return grade === "ALL" ? t : t.filter((x) => gradeOf(x.product || "") === grade);
  }, [charts, grade]);

  if (!kpi || !charts) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const marginTone = kpi.gross_margin >= 0 ? "green" : "red";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900">Executive Control Room</h1>
          <p className="text-sm text-slate-500 mt-0.5">Single-source view of petroleum, money and controls · USD · Gallons</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400 tabular" data-testid="dashboard-updated">Updated {updatedAt ? relTime(new Date(updatedAt).toISOString()) : "—"}</span>
          <button onClick={() => setLive((v) => !v)} data-testid="dashboard-live-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${live ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-slate-200 text-slate-500"}`}>
            {live ? <CircleDot className="h-3.5 w-3.5 animate-pulse" /> : <Radio className="h-3.5 w-3.5" />} {live ? "Live" : "Paused"}
          </button>
          <button onClick={fetchAll} disabled={refreshing} data-testid="dashboard-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
        <Kpi label="Purchased" value={fmtNum(kpi.purchased)} unit="Gallons" icon={Fuel} tone="navy" testid="kpi-purchased" onClick={() => navigate("/spas")} />
        <Kpi label="In Transfer" value={fmtNum(kpi.in_transfer)} unit="Gallons" icon={ArrowLeftRight} tone="amber" testid="kpi-in-transfer" onClick={() => navigate("/lprc-transfers")} />
        <Kpi label="In Storage" value={fmtNum(kpi.in_storage)} unit="Gallons" icon={Warehouse} tone="blue" testid="kpi-in-storage" onClick={() => navigate("/tanks")} />
        <Kpi label="Available" value={fmtNum(kpi.available)} unit="Gallons" icon={Boxes} tone="green" testid="kpi-available" onClick={() => navigate("/tanks")} />
        <Kpi label="Sold" value={fmtNum(kpi.sold)} unit="Gallons" icon={ShoppingCart} tone="navy" testid="kpi-sold" onClick={() => navigate("/sales-orders")} />
        <Kpi label="Delivered" value={fmtNum(kpi.delivered)} unit="Gallons" icon={PackageCheck} tone="blue" testid="kpi-delivered" onClick={() => navigate("/deliveries")} />
        <Kpi label="Reserved" value={fmtNum(kpi.reserved)} unit="Gallons" icon={Truck} tone="amber" testid="kpi-reserved" onClick={() => navigate("/tanks")} />
        <Kpi label="Revenue" value={fmtMoney(kpi.revenue)} icon={DollarSign} tone="green" testid="kpi-revenue" onClick={() => navigate("/sales-orders")} />
        <Kpi label="Cost" value={fmtMoney(kpi.cost)} icon={Wallet} tone="slate" testid="kpi-cost" onClick={() => navigate("/landed-cost")} />
        <Kpi label="Gross Margin" value={fmtMoney(kpi.gross_margin)} icon={kpi.gross_margin >= 0 ? TrendingUp : TrendingDown} tone={marginTone} testid="kpi-gross-margin" onClick={() => navigate("/landed-cost")} />
        <Kpi label="Receivables" value={fmtMoney(kpi.receivables)} icon={ReceiptText} tone="blue" testid="kpi-receivables" onClick={() => navigate("/customer-payments")} />
        <Kpi label="Payables" value={fmtMoney(kpi.payables)} icon={Wallet} tone="amber" testid="kpi-payables" onClick={() => navigate("/supplier-payments")} />
        <Kpi label="Pending Approvals" value={fmtNum(kpi.pending_approvals)} icon={CheckSquare} tone="navy" testid="kpi-pending-approvals" onClick={() => navigate("/approvals")} />
        <Kpi label="Exceptions" value={fmtNum(kpi.exceptions)} icon={AlertTriangle} tone="red" testid="kpi-exceptions" onClick={() => navigate("/exceptions")} />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-slate-400 mr-1">Grade</span>
        <Seg testidPrefix="grade-filter" value={grade} onChange={setGrade}
          options={[{ v: "ALL", l: "All" }, ...GRADES.map((g) => ({ v: g, l: g }))]} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head text-sm font-semibold text-slate-800">Petroleum Flow (Gallons)</h3>
            <div className="flex items-center gap-2">
              <Seg testidPrefix="flow-type" value={chartType} onChange={setChartType} options={[{ v: "bar", l: "Bar" }, { v: "area", l: "Area" }]} />
              <Seg testidPrefix="flow-range" value={range} onChange={setRange} options={[{ v: 3, l: "3M" }, { v: 6, l: "6M" }, { v: 12, l: "12M" }, { v: 0, l: "All" }]} />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            {chartType === "bar" ? (
              <BarChart data={flowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
                <Tooltip formatter={(v) => `${fmtNum(v)} L`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="receipts" name="Receipts" fill="#12346B" radius={[3, 3, 0, 0]} />
                <Bar dataKey="liftings" name="Liftings" fill="#F47920" radius={[3, 3, 0, 0]} />
              </BarChart>
            ) : (
              <AreaChart data={flowData}>
                <defs>
                  <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#12346B" stopOpacity={0.4} /><stop offset="95%" stopColor="#12346B" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gL" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F47920" stopOpacity={0.4} /><stop offset="95%" stopColor="#F47920" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
                <Tooltip formatter={(v) => `${fmtNum(v)} L`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="receipts" name="Receipts" stroke="#12346B" fill="url(#gR)" strokeWidth={2} />
                <Area type="monotone" dataKey="liftings" name="Liftings" stroke="#F47920" fill="url(#gL)" strokeWidth={2} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="font-head text-sm font-semibold text-slate-800 mb-3">Inventory by Grade</h3>
          {invData.length === 0 ? <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">No stock for {grade}</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={invData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {invData.map((e) => <Cell key={e.name} fill={pieColor(e.name)} />)}
                </Pie>
                <Tooltip formatter={(v) => `${fmtNum(v)} L`} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v.split(" ")[0]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4 lg:col-span-2">
          <h3 className="font-head text-sm font-semibold text-slate-800 mb-4">Tank Storage Levels · Freeport Monrovia Terminal</h3>
          {tankData.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">No tanks for {grade}</div> : (
            <div className="flex flex-wrap gap-6">
              {tankData.map((t) => <TankGauge key={t.tank_no} t={t} />)}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="activity-feed">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-[#F47920]" />
            <h3 className="font-head text-sm font-semibold text-slate-800">Live Activity</h3>
            {live && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600"><CircleDot className="h-2.5 w-2.5 animate-pulse" /> streaming</span>}
          </div>
          {activity.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">No recent activity.</div> : (
            <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
              {activity.map((a, i) => (
                <div key={`${a.at}-${i}`} data-testid={`activity-item-${i}`} className="flex items-start gap-2 text-xs">
                  <CircleDot className={`h-3 w-3 mt-0.5 shrink-0 ${ACTION_TONE[a.action] || "text-slate-400"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-700"><span className="font-semibold capitalize">{a.action.replace(/_/g, " ")}</span> · <span className="text-slate-500">{a.entity.replace(/_/g, " ")}</span></div>
                    <div className="text-[10px] text-slate-400 truncate">{a.by} · {relTime(a.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
